import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Loader2, RefreshCw, Wifi, WifiOff, CheckCircle2, LifeBuoy,
} from 'lucide-react';
import { BottomSheet } from '@evi/ui';
import { EvieOrb } from '@/components/primitives';
import {
  fetchMyChargerAlerts, clearChargerAlert, nudgeCharger, type MyChargerAlert,
} from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { useTourStore } from '@/store/tour';
import { toast } from '@/store/toast';
import styles from './ChargerAlertGreeter.module.css';

/**
 * Proactive EVII alert dialog for a home charger that needs attention.
 *
 * Mounted app-wide (AppShell). When a resident owner has an active
 * predictive-maintenance alert on a charger they own, EVII greets them
 * on app open with a dialog: the charger + its live status, and the
 * path back. While offline she points them at their own internet, tells
 * them we're already watching for the reconnection, and offers a nudge
 * (or the full guided recovery on the charger page). Once the charger
 * is back online the owner clears the alert themselves — the "feeling
 * of control" Warren asked for. The list polls, so an offline -> online
 * flip turns the guidance into a Clear button without a refresh.
 *
 * Greets once per alert per tab session (sessionStorage), so it's
 * proactive without nagging; the bell + the charger page remain the
 * always-available paths back to it.
 */
export function ChargerAlertGreeter({ onNavigate }: { onNavigate: (path: string) => void }) {
  const { t } = useTranslation('chrome');
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const queryClient = useQueryClient();

  const alertsQ = useQuery({
    queryKey: ['my-charger-alerts'],
    queryFn: fetchMyChargerAlerts,
    enabled: isAuthenticated,
    // Poll so a reconnection surfaces the Clear button on its own. The
    // charger page's guided recovery carries a faster 10s poll for the
    // owner who's actively working the fix there.
    refetchInterval: 20_000,
    refetchOnWindowFocus: true,
  });

  // Surface the most pressing home-charger alert: an offline one first,
  // else the newest (list is already detected-desc from the server).
  const alert: MyChargerAlert | null = useMemo(() => {
    const home = (alertsQ.data ?? []).filter((a) => a.charger.is_residential);
    return home.find((a) => !a.charger.is_online) ?? home[0] ?? null;
  }, [alertsQ.data]);

  const [open, setOpen] = useState(false);
  const [nudging, setNudging] = useState(false);
  const [clearing, setClearing] = useState(false);

  // Greet on app open — once per alert per tab session, and never over
  // the first-run guided tour (which owns the screen for new users).
  const alertId = alert?.alert_id ?? null;
  useEffect(() => {
    if (!alertId) return;
    if (useTourStore.getState().active) return;
    const key = `evi.alert-greeted.${alertId}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch {
      // private mode / quota — fall through and greet anyway (in-memory
      // open state still prevents a re-open within this mount).
    }
    setOpen(true);
  }, [alertId]);

  if (!alert) return null;

  const online = alert.charger.is_online;
  const chargerId = alert.charger.id;
  const label = alert.charger.label;

  const nudge = async (): Promise<void> => {
    setNudging(true);
    try {
      const res = await nudgeCharger(chargerId);
      toast(
        res.connected ? t('alert.toastWaking') : t('alert.toastWatching'),
        { tone: 'accent' },
      );
      queryClient.invalidateQueries({ queryKey: ['my-charger-alerts'] });
    } catch (err) {
      toast(err instanceof Error ? err.message : t('alert.toastNudgeError'), { tone: 'error' });
    } finally {
      setNudging(false);
    }
  };

  const clear = async (): Promise<void> => {
    setClearing(true);
    try {
      await clearChargerAlert(alert.alert_id);
      toast(t('alert.toastCleared'), { tone: 'accent' });
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ['my-charger-alerts'] });
    } catch (err) {
      toast(err instanceof Error ? err.message : t('alert.toastClearError'), { tone: 'error' });
    } finally {
      setClearing(false);
    }
  };

  const openRecovery = (): void => {
    setOpen(false);
    onNavigate(`/my-chargers/${chargerId}`);
  };

  return (
    <BottomSheet
      isOpen={open}
      onClose={() => setOpen(false)}
      title={online ? t('alert.titleOnline') : t('alert.titleOffline')}
      backdropTone="brand"
      titleIcon={<EvieOrb size={26} />}
    >
      <div className={styles.body}>
        <div className={styles.statusRow}>
          {online
            ? <Wifi size={16} className={styles.online} />
            : <WifiOff size={16} className={styles.offline} />}
          <span className={styles.chargerName}>{label}</span>
          <span className={online ? styles.online : styles.offline}>
            {online ? t('alert.statusOnline') : t('alert.statusOffline')}
          </span>
        </div>

        {!online ? (
          <>
            <p className={styles.evii}>
              {t('alert.offlineBody', { label })}
            </p>
            <button
              type="button"
              className={styles.primary}
              onClick={nudge}
              disabled={nudging}
            >
              {nudging ? <Loader2 size={16} className={styles.spin} /> : <RefreshCw size={16} />}
              {t('alert.nudge')}
            </button>
            <button type="button" className={styles.ghost} onClick={openRecovery}>
              <LifeBuoy size={15} /> {t('alert.walkThrough')}
            </button>
          </>
        ) : (
          <>
            <p className={styles.evii}>
              {t('alert.onlineBody', { label })}
            </p>
            <button
              type="button"
              className={styles.primary}
              onClick={clear}
              disabled={clearing}
            >
              {clearing ? <Loader2 size={16} className={styles.spin} /> : <CheckCircle2 size={16} />}
              {t('alert.clear')}
            </button>
          </>
        )}
      </div>
    </BottomSheet>
  );
}
